<script setup>
import {ref, inject, watch, markRaw} from 'vue';
    import { debounce } from 'lodash';

    import SubmitButtonLargeComponent from '@buttons/SubmitButtonLargeComponent.vue';
    import { showSuccessToast, showErrorToast } from '@utils/Toast';
    import { EnvelopeIcon, LockClosedIcon } from '@heroicons/vue/20/solid';
    import MainSiteLogo from '../Includes/menu/MainSiteLogo.vue';
    import {handleErrorResponse} from '@utils/errorHandler';
    import ThankYouFormSubmitted from './Popup/ThankYouFormSubmitted.vue';

    const props = defineProps({
        csrf: String,
        route: String,
        routeSuccess: String,
        routeLogin: String,
        routeRegister: String,
    });

    // Inject Axios
    const axios = inject('axios');

    const modalMessageState = inject('modalMessageState');

    const nonReactiveComponent = markRaw(ThankYouFormSubmitted);

    //Handle with form
    const email = ref('');
    const isFormValid = ref(false);

    const errors = ref({
        email: null,
    });

    const openThankYouDialog = (data) => {
        modalMessageState.open(
            nonReactiveComponent, {
                modalClass: 'xl:max-w-4xl',
                text: data.text,
            },
            () => {
                modalMessageState.close();
                window.location.href = '/login'; // Redirect to login page after closing the dialog
            }
        )
    };

    const validateForm = () => {
        // Check the validity of the input fields
        const isEmailValid = email.value !== '';

        errors.value.email = !isEmailValid ? 'Email address is required' : null;

        isFormValid.value = isEmailValid;
    };

    const submitForm = async () => {
        validateForm();
        if(!isFormValid.value) {
            // showErrorToast('Invalid form values');
            return false;
        }
        //Prepare the data to be sent
        const formData = {
            email: email.value,
        };

        try {
            //Send a POST request to the server
            const response = await axios.post(props.route, formData, {
                headers: {
                    'X-CSRF-TOKEN': props.csrf // Include CSRF token here
                }
            });
            //Handle response here
            if(response.status === 201) {
                const data = response.data;
                if(data.success === 1) {
                    openThankYouDialog(data);
                } else {
                    showErrorToast(data.message);
                }
            } else {
                if(response.message) {
                    showErrorToast(response.message);
                }
            }

        } catch (error) {
            showErrorToast(handleErrorResponse(error));
            console.error('Submission error', error);
        }
    };

</script>

<template>
    <div>
        <div class="text-center container max-w-100 md:w-100 bg-white rounded-xl mb-8">
            <div class="wrap-form mb-2 lg:mb-4">
                <form :action="route" id="frmClientLogin" method="post"
                      :data-routesucces="routeSuccess"
                      @submit.prevent="submitForm"
                >
                    <div class="flex flex-col justify-between py-6 mb-6 md:mb-0">
                        <div>
                            <div class="font-bold text-3xl-plus mb-6 text-center leading-tight">Create an Account</div>

                            <div class="form-row">
                                <div class="relative">
                                    <div
                                        class="absolute top-0 left-0 h-full w-10 z-20 flex items-center justify-center">
                                        <envelope-icon class="w-6 text-gray-2"></envelope-icon>
                                    </div>
                                    <input type="email"
                                           v-model="email"
                                           name="email"
                                           class="form-control z-10 px-8"
                                           placeholder="Enter your email address"
                                           autocomplete="off"
                                    ></div>
                                <div v-if="errors.email" class="py-2 text-red text-left" v-text="errors.email"></div>
                            </div>
                        </div>
                        <div>
                            <SubmitButtonLargeComponent
                                title="Create Account"
                                btn-class="btn-green-5 w-full !py-4"
                                text-size="text-base"
                            ></SubmitButtonLargeComponent>
                        </div>
                    </div>
                </form>
            </div>
            <div class="text-dark text-xl">
                Already have an account, <a :href="routeLogin" class="underline">Login</a> now!
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.form {
    &-row {
        @apply mb-4;
    }
    &-control {
        @apply bg-white py-3 ps-10;
        &::placeholder {
            @apply text-gray-2;
        }
    }
}
</style>
